import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import InfoIcon from '@mui/icons-material/Info';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import SwipeableViews from 'react-swipeable-views-react-18-fix';
import { useTranslation } from 'react-i18next';
import { Card, IconButton } from '@totalsoft/rocket-ui';
import Clear from '@mui/icons-material/Clear';
import { withRights } from 'providers/RightsConsumer';
import { RightsContext } from 'providers/RightsContext';
import { OfferMessageContent, OfferMessageWrapper, OfferMessagesWrapper } from './OfferWizardStyles';
import ThinDivider from 'components/common/ThinDivider';

const DisplaySummary = ({ messageTypeId, warnings, disabled }) => {
  const { t } = useTranslation();
  const filteredWarnings = warnings.filter(x => x.messageTypeId === messageTypeId);

  const errorMessage = useMemo(() => {
    switch (messageTypeId) {
      case 1:
        return t('UserMessages.Warnings');
      case 2:
        return t('UserMessages.Errors');
      case 4:
        return t('UserMessages.Messages');
      default:
        return '';
    }
  }, [messageTypeId, t]);

  return (
    <Grid container justifyContent='center' alignItems='center' sx={{ gap: 1 }}>
      <DisplayIcon messageTypeId={messageTypeId} disabled={disabled} />
      <span>{`${filteredWarnings.length} ${errorMessage}`}</span>
    </Grid>
  );
};

DisplaySummary.propTypes = {
  warnings: PropTypes.array,
  messageTypeId: PropTypes.string,
  disabled: PropTypes.bool
};

const OfferWarnings = ({ warnings, warningsActiveTab, setWarningsActiveTab, handleDismissDocumentWorkflowError }) => {
  const { t } = useTranslation();
  const { documentWarningsAreaErrorRead } = useContext(RightsContext);

  const handleChange = useCallback(
    newValue => () => {
      if (warningsActiveTab === newValue) setWarningsActiveTab(-1);
      else setWarningsActiveTab(newValue);
    },
    [setWarningsActiveTab, warningsActiveTab]
  );

  const getUserMessageId = messageTypeId => {
    switch (messageTypeId) {
      case 1:
        return t('UserMessages.Warnings').toLowerCase();
      case 2:
        return t('UserMessages.Errors').toLowerCase();
      case 4:
        return t('UserMessages.Messages').toLowerCase();
      default:
        return;
    }
  };

  const warningIds = [4, 1, 2];

  return (
    <Card id={'offerUserMessages'} disablePadding>
      <Tabs value={warningsActiveTab >= 0 ? warningsActiveTab : false} variant='fullWidth' indicatorColor='primary'>
        {warningIds.map((warningId, index) => {
          const disabled = warnings.filter(x => x.messageTypeId === warningId).length === 0;
          return warningId === 2 ? (
            documentWarningsAreaErrorRead && (
              <Tab
                id={'offerUserMessages_' + getUserMessageId(warningId)}
                key={warningId}
                icon={<DisplaySummary messageTypeId={warningId} warnings={warnings} disabled={disabled} />}
                onClick={handleChange(index)}
                disabled={disabled}
              ></Tab>
            )
          ) : (
            <Tab
              id={'offerUserMessages_' + getUserMessageId(warningId)}
              key={warningId}
              icon={<DisplaySummary messageTypeId={warningId} warnings={warnings} disabled={disabled} />}
              onClick={handleChange(index)}
              disabled={disabled}
            ></Tab>
          );
        })}
      </Tabs>
      {warningsActiveTab !== -1 && <ThinDivider />}
      <SwipeableViews index={warningsActiveTab >= 0 ? warningsActiveTab : undefined} onChangeIndex={handleChange}>
        {warningIds.map((warningId, index) => {
          return warningId === 2 ? (
            documentWarningsAreaErrorRead && (
              <WarningsContent
                warnings={warnings}
                key={warningId}
                warningsActiveTab={warningsActiveTab}
                index={index}
                messageTypeId={warningId}
                userMessageId={getUserMessageId(warningId)}
                handleDismissDocumentWorkflowError={handleDismissDocumentWorkflowError}
              ></WarningsContent>
            )
          ) : (
            <WarningsContent
              warnings={warnings}
              key={warningId}
              warningsActiveTab={warningsActiveTab}
              index={index}
              messageTypeId={warningId}
              userMessageId={getUserMessageId(warningId)}
              handleDismissDocumentWorkflowError={handleDismissDocumentWorkflowError}
            ></WarningsContent>
          );
        })}
      </SwipeableViews>
    </Card>
  );
};

OfferWarnings.propTypes = {
  warnings: PropTypes.array,
  setWarningsActiveTab: PropTypes.func,
  warningsActiveTab: PropTypes.any.isRequired,
  handleDismissDocumentWorkflowError: PropTypes.func.isRequired
};

const Warnings = withRights(OfferWarnings);

const DisplayIcon = ({ messageTypeId, disabled }) => {
  const sx = useMemo(() => ({ height: '25px', opacity: disabled ? 0.7 : 1 }), [disabled]);

  switch (messageTypeId) {
    case 1:
      return <WarningRoundedIcon color='warning' sx={sx} />;
    case 2:
      return <ErrorIcon color='error' sx={sx} />;
    case 4:
      return <InfoIcon color='primary' sx={sx}></InfoIcon>;
    default:
      return <></>;
  }
};

const getMessageColor = messageTypeId => {
  switch (messageTypeId) {
    case 1:
      return 'warning';
    case 2:
      return 'error';
    case 4:
    default:
      return 'primary';
  }
};

DisplayIcon.propTypes = {
  messageTypeId: PropTypes.number,
  disabled: PropTypes.bool
};

const OfferWarningsContent = ({ warnings, warningsActiveTab, index, messageTypeId, userMessageId, handleDismissDocumentWorkflowError }) => {
  const { t } = useTranslation();

  return (
    <OfferMessagesWrapper active={warningsActiveTab >= 0}>
      {warningsActiveTab === index &&
        warnings
          .filter(x => x.messageTypeId === messageTypeId)
          .map((n, index) => {
            return (
              <div id={'offerUserMessages_' + userMessageId + '_content'} key={index}>
                <OfferMessageWrapper>
                  <Grid item>
                    <DisplayIcon messageTypeId={n.messageTypeId} />
                  </Grid>
                  <OfferMessageContent color={getMessageColor(n.messageTypeId)}>
                    {n.dismissible ? t('Warnings.DocumentWorkflowError', { message: n.message }) : n.message}
                  </OfferMessageContent>
                  {n.dismissible && (
                    <Grid item>
                      <IconButton
                        size='tiny'
                        variant='text'
                        color={getMessageColor(n.messageTypeId)}
                        onClick={handleDismissDocumentWorkflowError(n.documentWorkflowErrorId)}
                        tooltip={t('Warnings.DismissError')}
                      >
                        <Clear fontSize='small' />
                      </IconButton>
                    </Grid>
                  )}
                </OfferMessageWrapper>
              </div>
            );
          })}
    </OfferMessagesWrapper>
  );
};

OfferWarningsContent.propTypes = {
  warnings: PropTypes.array,
  warningsActiveTab: PropTypes.any.isRequired,
  index: PropTypes.any.isRequired,
  messageTypeId: PropTypes.number.isRequired,
  setWarningsActiveTab: PropTypes.func,
  userMessageId: PropTypes.string,
  handleDismissDocumentWorkflowError: PropTypes.func.isRequired
};

const WarningsContent = OfferWarningsContent;

export default Warnings;
